<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import CsatMetricCard from './CsatMetricCard.vue';

const props = defineProps({
  // Meta CES: nota máxima desejada (escala invertida - quanto menor, melhor)
  targetScore: { type: Number, default: null },
});

const { t } = useI18n();
const ces = useMapGetter('csat/getCesMetrics');
const uiFlags = useMapGetter('csat/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetchingMetrics);
const formatNumber = v => Number(v || 0).toLocaleString('pt-BR');

const totalResponses = computed(() => ces.value?.total || 0);
const avgScore = computed(() => Number(ces.value?.average || 0));
const distribution = computed(() => ces.value?.distribution || {});

// Escala CES vai de 1 (muito fácil) a 7 (muito difícil). Quanto menor a média, melhor.
const scoreColor = computed(() => {
  const v = avgScore.value;
  if (v === 0) return 'text-n-slate-11';
  if (v <= 2.5) return 'text-n-grass-11';
  if (v <= 4.5) return 'text-n-amber-11';
  return 'text-n-ruby-11';
});

const targetStatus = computed(() => {
  if (props.targetScore == null) return null;
  if (totalResponses.value === 0) return null;
  const v = avgScore.value;
  const target = props.targetScore;
  // Em CES, menor é melhor - meta é teto.
  if (v <= target)
    return {
      color: 'text-n-grass-11',
      icon: 'i-lucide-check',
      label: t('CES_REPORTS.METRIC.TARGET.MET', { target }),
    };
  if (v <= target + 1)
    return {
      color: 'text-n-amber-11',
      icon: 'i-lucide-trending-up',
      label: t('CES_REPORTS.METRIC.TARGET.NEAR', { target }),
    };
  return {
    color: 'text-n-ruby-11',
    icon: 'i-lucide-trending-down',
    label: t('CES_REPORTS.METRIC.TARGET.BELOW', { target }),
  };
});

const distributionPct = computed(() => {
  const total = totalResponses.value || 1;
  return Array.from({ length: 7 }, (_, i) => {
    const score = i + 1;
    const count =
      distribution.value[score] || distribution.value[String(score)] || 0;
    return { score, count, pct: ((count / total) * 100).toFixed(1) };
  });
});

const scoreColorByValue = score => {
  if (score <= 2) return 'bg-n-grass-9';
  if (score <= 5) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      class="flex sm:flex-row flex-col w-full gap-4 sm:gap-14 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <CsatMetricCard
        :label="t('CES_REPORTS.METRIC.TOTAL_RESPONSES')"
        :tooltip="t('CES_REPORTS.METRIC.TOTAL_RESPONSES_TOOLTIP')"
        :value="formatNumber(totalResponses)"
        :is-loading="isLoading"
      />

      <div class="w-full sm:w-px bg-n-strong" />

      <div class="flex-1">
        <CsatMetricCard
          :label="t('CES_REPORTS.METRIC.AVERAGE_SCORE')"
          :tooltip="t('CES_REPORTS.METRIC.AVERAGE_SCORE_TOOLTIP')"
          :value="avgScore.toFixed(2)"
          :value-class="scoreColor"
          :is-loading="isLoading"
        />
        <p
          v-if="targetStatus && !isLoading"
          class="mt-1.5 inline-flex items-center gap-1 text-body-small"
          :class="targetStatus.color"
        >
          <i :class="targetStatus.icon" class="size-3.5" />
          {{ targetStatus.label }}
        </p>
      </div>
    </div>

    <!-- Distribuição 1-7 -->
    <div
      class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
    >
      <h3 class="text-heading-3 text-n-slate-12 mb-3">
        {{ t('CES_REPORTS.DISTRIBUTION.TITLE') }}
      </h3>

      <div v-if="totalResponses === 0" class="text-body-main text-n-slate-11">
        {{ t('CES_REPORTS.EMPTY') }}
      </div>

      <div v-else class="flex flex-col gap-2">
        <div
          v-for="row in distributionPct"
          :key="`ces-${row.score}`"
          class="grid grid-cols-[2.5rem,1fr,5rem] items-center gap-3"
        >
          <span class="text-body-small font-semibold text-n-slate-12">{{
            row.score
          }}</span>
          <div class="h-3 rounded-md bg-n-alpha-2 overflow-hidden">
            <div
              class="h-full transition-all"
              :class="scoreColorByValue(row.score)"
              :style="{ width: `${row.pct}%` }"
            />
          </div>
          <span class="text-body-small text-n-slate-11 text-right">
            {{ formatNumber(row.count) }} · {{ row.pct }}%
          </span>
        </div>

        <p class="text-label-small text-n-slate-10 mt-2 mb-0">
          {{ t('CES_REPORTS.DISTRIBUTION.HINT') }}
        </p>
      </div>
    </div>
  </div>
</template>
